import { useRef, useEffect, useCallback, useState } from 'react';
import logger from '../utils/logger';

const stabilityLogger = logger.create('Stability');

/**
 * useSafeAsync - Hook for safe async operations that handles component unmounting
 * Prevents "setState on unmounted component" warnings
 */
export const useSafeAsync = () => {
  const mountedRef = useRef(true);
  
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  
  const safeSetState = useCallback((setter) => {
    if (mountedRef.current) {
      setter();
    }
  }, []);
  
  const safeAsync = useCallback(async (asyncFn) => {
    try {
      const result = await asyncFn();
      if (mountedRef.current) {
        return result;
      }
      return null;
    } catch (error) {
      if (mountedRef.current) {
        throw error;
      }
      return null;
    }
  }, []);
  
  return { isMounted: () => mountedRef.current, safeSetState, safeAsync };
};

/**
 * useDebounce - Debounce a value
 * @param {*} value - Value to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {*} Debounced value
 */
export const useDebounce = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(timer);
  }, [value, delay]);
  
  return debouncedValue;
};

/**
 * useThrottle - Throttle a value
 * @param {*} value - Value to throttle
 * @param {number} limit - Limit in milliseconds
 * @returns {*} Throttled value
 */
export const useThrottle = (value, limit = 100) => {
  const [throttledValue, setThrottledValue] = useState(value);
  const lastRan = useRef(null);
  
  // Initialize lastRan in effect
  useEffect(() => {
    if (lastRan.current === null) {
      lastRan.current = Date.now();
    }
  }, []);
  
  useEffect(() => {
    const now = Date.now();
    const timeSinceLastRan = lastRan.current ? now - lastRan.current : limit;
    
    const handler = setTimeout(() => {
      if (timeSinceLastRan >= limit) {
        setThrottledValue(value);
        lastRan.current = Date.now();
      }
    }, Math.max(0, limit - timeSinceLastRan));
    
    return () => clearTimeout(handler);
  }, [value, limit]);
  
  return throttledValue;
};

/**
 * usePrevious - Get previous value of a variable
 * @param {*} value - Current value
 * @returns {*} Previous value
 */
export const usePrevious = (value) => {
  const ref = useRef();
  const [previousValue, setPreviousValue] = useState();
  
  useEffect(() => {
    setPreviousValue(ref.current);
    ref.current = value;
  }, [value]);
  
  return previousValue;
};

/**
 * useOnClickOutside - Detect clicks outside an element
 * @param {RefObject} ref - Ref to the element
 * @param {Function} handler - Handler to call when clicked outside
 */
export const useOnClickOutside = (ref, handler) => {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      handler(event);
    };
    
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
};

/**
 * useEventListener - Safely add event listeners
 * @param {string} eventName - Event name
 * @param {Function} handler - Event handler
 * @param {Element} element - Element to attach to (default: window)
 * @param {Object} options - Event listener options
 */
export const useEventListener = (eventName, handler, element = null, options = {}) => {
  const savedHandler = useRef(handler);
  
  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);
  
  useEffect(() => {
    const targetElement = element || window;
    if (!targetElement?.addEventListener) return;
    
    const eventListener = (event) => savedHandler.current(event);
    
    targetElement.addEventListener(eventName, eventListener, options);
    
    return () => {
      targetElement.removeEventListener(eventName, eventListener, options);
    };
  }, [eventName, element, options]);
};

/**
 * useLocalStorage - Sync state with localStorage
 * @param {string} key - Storage key
 * @param {*} initialValue - Initial value
 * @returns {[*, Function]} Value and setter
 */
export const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    if (typeof window === 'undefined') return initialValue;
    
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      stabilityLogger.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });
  
  const setValue = useCallback((value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch (error) {
      stabilityLogger.warn(`Error setting localStorage key "${key}":`, error);
    }
  }, [key, storedValue]);
  
  return [storedValue, setValue];
};

/**
 * useWindowSize - Track window dimensions
 * @returns {{ width: number, height: number }} Window dimensions
 */
export const useWindowSize = () => {
  const [size, setSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0
  });
  
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleResize = () => {
      setSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return size;
};

/**
 * useOnlineStatus - Track online/offline status
 * @returns {boolean} True if online
 */
export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  return isOnline;
};

/**
 * useInterval - Safe interval hook with cleanup
 * @param {Function} callback - Callback to run
 * @param {number|null} delay - Delay in ms (null to pause)
 */
export const useInterval = (callback, delay) => {
  const savedCallback = useRef(callback);
  
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);
  
  useEffect(() => {
    if (delay === null) return;
    
    const id = setInterval(() => savedCallback.current(), delay);
    return () => clearInterval(id);
  }, [delay]);
};

/**
 * useTimeout - Safe timeout hook with cleanup
 * @param {Function} callback - Callback to run
 * @param {number|null} delay - Delay in ms (null to cancel)
 */
export const useTimeout = (callback, delay) => {
  const savedCallback = useRef(callback);
  
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);
  
  useEffect(() => {
    if (delay === null) return;
    
    const id = setTimeout(() => savedCallback.current(), delay);
    return () => clearTimeout(id);
  }, [delay]);
};

/**
 * useIsFirstRender - Check if this is the first render
 * Uses a state-based approach that is React Strict Mode compatible
 * @returns {boolean} True on first render
 */
export const useIsFirstRender = () => {
  const [isFirst, setIsFirst] = useState(true);
  
  useEffect(() => {
    // Use requestAnimationFrame to defer state update
    const raf = requestAnimationFrame(() => {
      setIsFirst(false);
    });
    return () => cancelAnimationFrame(raf);
  }, []);
  
  return isFirst;
};

/**
 * useUpdateEffect - useEffect that skips first render
 * @param {Function} effect - Effect function
 * @param {Array} deps - Dependencies
 */
export const useUpdateEffect = (effect, deps) => {
  const isFirstRender = useRef(true);
  
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    return effect();
  // eslint-disable-next-line
  }, deps);
};

export default {
  useSafeAsync,
  useDebounce,
  useThrottle,
  usePrevious,
  useOnClickOutside,
  useEventListener,
  useLocalStorage,
  useWindowSize,
  useOnlineStatus,
  useInterval,
  useTimeout,
  useIsFirstRender,
  useUpdateEffect
};
